/**
 * Shopify custom element section.
 *
 * Liquid renders <figma-promo-subscribe> and loads this file with `defer`, so the browser
 * upgrades the element on its own and this class owns the whole init / teardown lifecycle.
 * No DOMContentLoaded or shopify:section:load wiring is needed: when the section is
 * re-rendered in the theme editor the node is replaced, so the old instance is torn down
 * and a fresh one is connected.
 *
 * The `customElements.get` guard is required, not decorative: a section can be placed more
 * than once on a page, which emits the same asset URL twice, and a tag name can never be
 * redefined once registered.
 */
class FigmaPromoSubscribe extends HTMLElement {
  connectedCallback() {
    // Moving an already upgraded node within the document re-runs connectedCallback.
    // Bind the listeners only on the first pass.
    if (this.dataset.promoSubscribeReady) {
      return;
    }

    this.dataset.promoSubscribeReady = 'true';

    const form = this.querySelector('form.figma-promo-subscribe__form');
    const status = this.querySelector('[data-promo-subscribe-status]');
    const submit = this.querySelector('[data-promo-subscribe-submit]');

    if (!form || !status || !submit) {
      return;
    }

    const email = this.querySelector('.figma-promo-subscribe__email');
    const label = submit.querySelector('.figma-promo-subscribe__submit-label');
    const successMessage = submit.dataset.successMessage || 'Thanks for subscribing!';
    const errorMessage = submit.dataset.errorMessage || 'Please enter a valid email address.';

    // Rely on the inline validation below instead of the browser bubble.
    form.noValidate = true;

    function setStatus(state, message) {
      if (state) {
        status.dataset.state = state;
      } else {
        delete status.dataset.state;
      }

      status.textContent = message || '';
    }

    function setLoading(isLoading) {
      const defaultLabel = submit.dataset.defaultLabel || label?.textContent || '';
      const loadingLabel = submit.dataset.loadingLabel || defaultLabel;

      submit.disabled = isLoading;
      submit.classList.toggle('is-loading', isLoading);
      submit.setAttribute('aria-busy', String(isLoading));

      if (label) {
        label.textContent = isLoading ? loadingLabel : defaultLabel;
      }
    }

    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());
    }

    async function submitViaAjax() {
      // Read the endpoint from the form Shopify generated instead of hardcoding '/contact':
      // with Markets or a localized storefront the path carries a locale prefix.
      const action = form.getAttribute('action') || '/contact';

      const response = await fetch(action, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body: new URLSearchParams(new FormData(form)).toString()
      });

      // Shopify confirms a successful signup by redirecting (302) back to the page with
      // ?contact_posted=true appended. Detect that marker so we don't try to parse the
      // HTML the redirect lands on, and so we never silently treat a failure as success.
      if (response.url && response.url.indexOf('contact_posted=true') !== -1) {
        return { success: true };
      }

      if (!response.ok) {
        throw new Error(`Subscribe request failed with status ${response.status}`);
      }

      try {
        const result = await response.json();
        return { success: !result || !result.errors, errors: result && result.errors };
      } catch (parseError) {
        // Non-JSON (HTML) response without a success redirect: can't verify, treat as failure.
        return { success: false };
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (submit.disabled) {
        return;
      }

      const value = email?.value || '';

      if (!isValidEmail(value)) {
        setStatus('error', errorMessage);
        email?.focus();
        return;
      }

      setStatus(null, '');
      setLoading(true);

      try {
        const result = await submitViaAjax();

        if (!result.success) {
          setStatus('error', errorMessage);
          return;
        }

        setStatus('success', successMessage);
        form.reset();
      } catch (error) {
        console.log(error);
        setStatus('error', errorMessage);
      } finally {
        setLoading(false);
      }
    });

    email?.addEventListener('input', () => {
      if (status.dataset.state === 'error') {
        setStatus(null, '');
      }
    });
  }
}

if (!customElements.get('figma-promo-subscribe')) {
  customElements.define('figma-promo-subscribe', FigmaPromoSubscribe);
}
